<template>
  <addon-layout
    title="Travail de l’étudiant"
    :subtitle="attachment ? attachment.title : ''"
    :loading="loading"
    :error="error"
  >
    <section
      v-if="needsGoogleAuth"
      class="empty"
    >
      <h2>Se connecter avec Google</h2>
      <p>Confirmez le compte professeur actuellement ouvert dans Classroom.</p>
      <google-sign-in-button
        @click="connectGoogle"
      />
    </section>
    <section
      v-else-if="!error && !submission"
      class="empty"
    >
      L’étudiant n’a pas encore commencé ce parcours.
    </section>
    <template v-else-if="submission">
      <section class="summary">
        <div>
          <span>Étudiant</span>
          <strong>{{ submission.studentName }}</strong>
        </div>
        <div>
          <span>Progression</span>
          <strong>{{ submission.percentComplete }} %</strong>
        </div>
        <div>
          <span>Réponses justes</span>
          <strong>{{ submission.correctAnswers }} / {{ submission.gradedQuestions }}</strong>
        </div>
        <div>
          <span>Note Classroom</span>
          <strong>{{ submission.pointsEarned === null ? '—' : `${submission.pointsEarned} / 100` }}</strong>
        </div>
      </section>
      <section
        v-if="submission.lastGradeSyncError"
        class="sync-error"
      >
        <p>{{ submission.lastGradeSyncError }}</p>
        <button
          type="button"
          :disabled="retrying"
          @click="retryGrade"
        >
          {{ retrying ? 'Nouvel essai…' : 'Renvoyer la note' }}
        </button>
      </section>
      <section
        v-if="journey && journey.lessons"
        class="lessons"
      >
        <article
          v-for="lesson in journey.lessons"
          :key="lesson.lessonId"
        >
          <div>
            <strong>{{ lesson.title }}</strong>
            <span>{{ lesson.status }}</span>
          </div>
          <b>{{ lesson.score === null || lesson.score === undefined ? '—' : `${lesson.score} %` }}</b>
        </article>
      </section>
    </template>
  </addon-layout>
</template>

<script>
import {
  authorize,
  client,
  hasAddonSession,
  launchContext,
} from '@/services/googleClassroomAddon'
import AddonLayout from './AddonLayout.vue'
import GoogleSignInButton from './GoogleSignInButton.vue'

export default {
  name: 'GoogleClassroomAddonStudentWorkReview',
  components: { AddonLayout, GoogleSignInButton },
  data() {
    return {
      loading: true,
      retrying: false,
      error: '',
      attachment: null,
      submission: null,
      journey: null,
      needsGoogleAuth: !hasAddonSession(),
    }
  },
  async created() {
    if (hasAddonSession()) await this.load()
    else this.loading = false
  },
  methods: {
    async load() {
      this.error = ''
      try {
        const response = await client.post('/review', launchContext(this.$route))
        this.attachment = response.data.attachment
        this.submission = response.data.submission
        this.journey = response.data.journey
        this.needsGoogleAuth = false
      } catch (error) {
        this.error = error.response?.data?.error || error.message
        if (error.response && error.response.status === 401) this.needsGoogleAuth = true
      } finally {
        this.loading = false
      }
    },
    async connectGoogle() {
      this.loading = true
      this.error = ''
      try {
        await authorize(this.$route)
        await this.load()
      } catch (error) {
        this.error = error.response?.data?.error || error.message
        this.loading = false
      }
    },
    async retryGrade() {
      this.retrying = true
      try {
        const response = await client.post(
          `/submissions/${this.submission.id}/retry-grade`,
          launchContext(this.$route),
        )
        this.submission = { ...this.submission, ...response.data }
      } catch (error) {
        this.error = error.response?.data?.error || error.message
      } finally {
        this.retrying = false
      }
    },
  },
}
</script>

<style scoped>
.empty,
.summary,
.lessons,
.sync-error {
  padding: 18px;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: white;
}
.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 14px;
}
.summary div { display: flex; flex-direction: column; gap: 5px; }
.summary span,
.lessons span { color: #64748b; font-size: 12px; }
.summary strong { font-size: 18px; }
.lessons { margin-top: 14px; }
.lessons article {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #eef2f7;
}
.lessons article:last-child { border-bottom: 0; }
.lessons article div { display: flex; flex-direction: column; }
.sync-error { margin-top: 14px; background: #fff7ed; color: #9a3412; }
.sync-error button {
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: #9a3412;
  color: white;
}
</style>
